import { useQuery } from '@tanstack/react-query'
import { getFaqs } from '../data/api'
import { FAQS } from '../data/faqs'

/* The questions, wherever they are coming from.
 *
 * Two sources, one shape. The showroom files its questions in the console and
 * those are what the site shows; until it has — an empty table on the first
 * afternoon — and whenever the API cannot be reached, the twelve the site
 * ships with stand in. Both the home page's accordion and /financing read
 * through here, so the two never disagree about what the answer is.
 *
 * Every answer arrives as markup. The console's are HTML that the API
 * sanitised at write time; the built-in ones are plain sentences, escaped and
 * wrapped here — so a consumer renders one kind of thing and does not have to
 * ask which list it got.
 */

export type FaqEntry = {
  id: string
  question: string
  /** Safe to render. Sanitised by the API, or escaped below. */
  html: string
}

const escape = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/** The list the site ships with, in the shape the console's rows arrive in. */
const BUILT_IN: FaqEntry[] = FAQS.map((faq) => ({
  id: faq.id,
  question: faq.question,
  html: `<p>${escape(faq.answer)}</p>`,
}))

export const faqKeys = { all: () => ['faqs'] as const }

export function useFaqs() {
  /* Read on nearly every visit and written a handful of times a year, so the
     same five-minute freshness the catalogue gets. A change made in the
     console does not wait for it: the storefront's event stream drops this
     cache the moment a question is saved — see useCatalogueStream.

     One retry, not three: there is a complete list to fall back on, so a
     failure costs the visitor nothing worth a spinner. */
  const query = useQuery({
    queryKey: faqKeys.all(),
    queryFn: getFaqs,
    staleTime: 5 * 60_000,
    retry: 1,
  })

  const rows = query.data ?? []
  const live = rows.length > 0

  return {
    faqs: live
      ? rows.map((row) => ({ id: row.id, question: row.question, html: row.answer }))
      : BUILT_IN,
    /** True while the site is showing its own list rather than the desk's. */
    builtIn: !live,
  }
}
